/** Canvas padding for Editing Tools prep — separate from batch editor layout. */

import { getTransform } from "./getTransform";

export const PADDING_UNIT = {
  PERCENT: "percent",
  PX: "px",
};

export const DEFAULT_PADDING_SETTINGS = {
  unit: PADDING_UNIT.PERCENT,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  fillColor: "transparent",
  cornerRadius: 0,
};

/** @deprecated Use createDefaultPaddingSettings */
export const ZERO_CANVAS_PADDING = { ...DEFAULT_PADDING_SETTINGS };

export function createDefaultPaddingSettings() {
  return { ...DEFAULT_PADDING_SETTINGS };
}

/** True when prep settings include padding, fill, or corners beyond defaults. */
export function hasActivePaddingSettings(padding) {
  const s = normalizePaddingSettings(padding);
  const hasInset =
    s.top > 0 || s.right > 0 || s.bottom > 0 || s.left > 0;
  const hasFill = Boolean(s.fillColor && s.fillColor !== "transparent");
  const hasCorners = (Number(s.cornerRadius) || 0) > 0;
  return hasInset || hasFill || hasCorners;
}

export function clampCanvasPaddingPercent(value) {
  return Math.max(0, Math.min(90, Number(value) || 0));
}

export function clampCanvasPaddingPx(value, max = 4096) {
  return Math.max(0, Math.min(max, Number(value) || 0));
}

export function clampPaddingSide(value, unit = PADDING_UNIT.PERCENT, maxPx = 4096) {
  if (unit === PADDING_UNIT.PX) {
    return clampCanvasPaddingPx(value, maxPx);
  }
  return clampCanvasPaddingPercent(value);
}

/**
 * Normalize full padding settings for prep/editor.
 */
export function normalizePaddingSettings(padding) {
  if (!padding) return createDefaultPaddingSettings();

  const unit =
    padding.unit === PADDING_UNIT.PX ? PADDING_UNIT.PX : PADDING_UNIT.PERCENT;

  return {
    unit,
    top: clampPaddingSide(padding.top ?? padding.paddingTop, unit),
    right: clampPaddingSide(padding.right ?? padding.paddingRight, unit),
    bottom: clampPaddingSide(padding.bottom ?? padding.paddingBottom, unit),
    left: clampPaddingSide(padding.left ?? padding.paddingLeft, unit),
    fillColor:
      padding.fillColor == null || padding.fillColor === ""
        ? "transparent"
        : String(padding.fillColor),
    cornerRadius: clampPaddingSide(padding.cornerRadius, unit, 9999),
  };
}

/** @deprecated Use normalizePaddingSettings */
export function normalizeCanvasPadding(padding) {
  return normalizePaddingSettings(padding);
}

function isSymmetricPadding(padding) {
  const { top, right, bottom, left } = normalizePaddingSettings(padding);
  return top === right && right === bottom && bottom === left;
}

function computePaddingPixels(contentWidth, contentHeight, padding) {
  const w = Number(contentWidth) || 0;
  const h = Number(contentHeight) || 0;
  const settings = normalizePaddingSettings(padding);
  const { top, right, bottom, left, unit } = settings;

  if (isSymmetricPadding(padding) && top > 0) {
    if (unit === PADDING_UNIT.PX) {
      return { top, right: top, bottom: top, left: top };
    }
    const g = (Math.min(w, h) * top) / 100;
    return { top: g, right: g, bottom: g, left: g };
  }

  if (unit === PADDING_UNIT.PX) {
    return { top, right, bottom, left };
  }

  return {
    top: (h * top) / 100,
    right: (w * right) / 100,
    bottom: (h * bottom) / 100,
    left: (w * left) / 100,
  };
}

export function resolveCornerRadiusPx(padding, canvasWidth, canvasHeight) {
  const settings = normalizePaddingSettings(padding);
  const radius = Number(settings.cornerRadius) || 0;
  if (radius <= 0) return 0;

  const cw = Number(canvasWidth) || 0;
  const ch = Number(canvasHeight) || 0;
  const maxPx = Math.max(0, Math.min(cw, ch) / 2);

  if (settings.unit === PADDING_UNIT.PX) {
    return Math.min(radius, maxPx);
  }

  const minEdge = Math.min(cw, ch);
  if (!minEdge) return 0;
  return Math.min((minEdge * radius) / 100, maxPx);
}

/**
 * Inner image corner radii so the photo follows the outer canvas curve.
 * Uses distance from each image edge to the canvas edge (padding / letterbox).
 */
export function computeImageCornerRadii(outerRadiusPx, edgeInsets = {}) {
  const R = Math.max(0, Number(outerRadiusPx) || 0);
  if (!R) {
    return { tl: 0, tr: 0, br: 0, bl: 0 };
  }
  const top = Math.max(0, Number(edgeInsets.top) || 0);
  const right = Math.max(0, Number(edgeInsets.right) || 0);
  const bottom = Math.max(0, Number(edgeInsets.bottom) || 0);
  const left = Math.max(0, Number(edgeInsets.left) || 0);
  return {
    tl: Math.max(0, R - Math.max(top, left)),
    tr: Math.max(0, R - Math.max(top, right)),
    br: Math.max(0, R - Math.max(bottom, right)),
    bl: Math.max(0, R - Math.max(bottom, left)),
  };
}

export function formatPaddingCornerRadiiCss(radii, scale = 1) {
  if (!radii) return undefined;
  const tl = (radii.tl || 0) * scale;
  const tr = (radii.tr || 0) * scale;
  const br = (radii.br || 0) * scale;
  const bl = (radii.bl || 0) * scale;
  if (!tl && !tr && !br && !bl) return undefined;
  return `${tl}px ${tr}px ${br}px ${bl}px`;
}

function resolveLayoutImageCornerRadii(
  canvasWidth,
  canvasHeight,
  imageX,
  imageY,
  imageWidth,
  imageHeight,
  cornerRadiusPx
) {
  return computeImageCornerRadii(cornerRadiusPx, {
    top: imageY,
    left: imageX,
    right: Math.max(0, canvasWidth - imageX - imageWidth),
    bottom: Math.max(0, canvasHeight - imageY - imageHeight),
  });
}

/**
 * Pixel insets per side.
 */
export function computeCanvasPaddingInsets(contentWidth, contentHeight, padding) {
  return computePaddingPixels(contentWidth, contentHeight, padding);
}

/**
 * Canvas-expansion padding: image keeps its fitted size; canvas grows by padding.
 */
export function computePaddedCanvasLayout(
  viewportWidth,
  viewportHeight,
  imageWidth,
  imageHeight,
  padding
) {
  const vw = Number(viewportWidth) || 0;
  const vh = Number(viewportHeight) || 0;
  const iw = Number(imageWidth) || 0;
  const ih = Number(imageHeight) || 0;

  const empty = {
    canvasWidth: 0,
    canvasHeight: 0,
    imageX: 0,
    imageY: 0,
    imageWidth: 0,
    imageHeight: 0,
    cornerRadiusPx: 0,
    imageCornerRadii: { tl: 0, tr: 0, br: 0, bl: 0 },
    fillColor: "transparent",
  };

  if (!vw || !vh || !iw || !ih) return empty;

  const settings = normalizePaddingSettings(padding);
  const fit = getTransform(iw, ih, vw, vh, "fit");
  const imgW = fit.width;
  const imgH = fit.height;
  const imgX = fit.x;
  const imgY = fit.y;

  const hasPadding =
    settings.top || settings.right || settings.bottom || settings.left;

  if (!hasPadding) {
    const canvasWidth = vw;
    const canvasHeight = vh;
    const cornerRadiusPx = resolveCornerRadiusPx(
      settings,
      canvasWidth,
      canvasHeight
    );
    return {
      canvasWidth,
      canvasHeight,
      imageX: imgX,
      imageY: imgY,
      imageWidth: imgW,
      imageHeight: imgH,
      cornerRadiusPx,
      imageCornerRadii: resolveLayoutImageCornerRadii(
        canvasWidth,
        canvasHeight,
        imgX,
        imgY,
        imgW,
        imgH,
        cornerRadiusPx
      ),
      fillColor: settings.fillColor,
    };
  }

  const pads = computePaddingPixels(imgW, imgH, settings);
  const canvasWidth = imgW + pads.left + pads.right;
  const canvasHeight = imgH + pads.top + pads.bottom;
  const imageX = imgX + pads.left;
  const imageY = imgY + pads.top;
  const cornerRadiusPx = resolveCornerRadiusPx(
    settings,
    canvasWidth,
    canvasHeight
  );

  return {
    canvasWidth,
    canvasHeight,
    imageX,
    imageY,
    imageWidth: imgW,
    imageHeight: imgH,
    cornerRadiusPx,
    imageCornerRadii: resolveLayoutImageCornerRadii(
      canvasWidth,
      canvasHeight,
      imageX,
      imageY,
      imgW,
      imgH,
      cornerRadiusPx
    ),
    fillColor: settings.fillColor,
  };
}

/**
 * Image rect inside padded canvas — shared by prep preview and CanvasEditor.
 */
export function computePaddedImageTransform(
  canvasWidth,
  canvasHeight,
  imageWidth,
  imageHeight,
  padding
) {
  const layout = computePaddedCanvasLayout(
    canvasWidth,
    canvasHeight,
    imageWidth,
    imageHeight,
    padding
  );

  return {
    x: layout.imageX,
    y: layout.imageY,
    width: layout.imageWidth,
    height: layout.imageHeight,
    canvasWidth: layout.canvasWidth,
    canvasHeight: layout.canvasHeight,
    cornerRadiusPx: layout.cornerRadiusPx,
    imageCornerRadii: layout.imageCornerRadii,
    fillColor: layout.fillColor,
  };
}

/** @deprecated Use computePaddedCanvasLayout */
export function computePaddedContentRect(canvasWidth, canvasHeight, padding) {
  const insets = computeCanvasPaddingInsets(canvasWidth, canvasHeight, padding);
  const cw = Number(canvasWidth) || 0;
  const ch = Number(canvasHeight) || 0;
  return {
    x: insets.left,
    y: insets.top,
    width: Math.max(0, cw - insets.left - insets.right),
    height: Math.max(0, ch - insets.top - insets.bottom),
  };
}
